import React, { useState, useEffect } from 'react';
import { Save, Settings, FileText, Upload, Database, RefreshCw, CheckCircle, Smartphone, Monitor } from 'lucide-react';
import { ConfiguracoesService } from '../../services';
import toast from 'react-hot-toast';
import { Configuracao } from '../../types';
import PWASettings from '../../components/PWASettings';
import SistemaStats from '../../components/SistemaStats';

interface Configuration {
  nome_site: string;
  arquivos_permitidos: string;
  maximo_arquivos: string;
  tamanho_maximo: string;
}

const Configurations: React.FC = () => {
  const [config, setConfig] = useState<Configuration>({
    nome_site: '',
    arquivos_permitidos: '',
    maximo_arquivos: '',
    tamanho_maximo: ''
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isInitializing, setIsInitializing] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<'general' | 'pwa' | 'sistema'>('general');

  useEffect(() => {
    loadConfigurations();
  }, []);

  const loadConfigurations = async (): Promise<void> => {
    try {
      setIsLoading(true);
      
      // Carregar configurações da API
      const response = await ConfiguracoesService.getConfiguracoes();
      
      if (Array.isArray(response)) {
        // Mapear dados da API para o formato esperado
        const apiConfig = response.reduce((acc: any, item: Configuracao) => {
          acc[item.nome] = item.valor;
          return acc;
        }, {});
        
        setConfig({
          nome_site: apiConfig.nome_site || '',
          arquivos_permitidos: apiConfig.arquivos_permitidos || '',
          maximo_arquivos: apiConfig.maximo_arquivos || '',
          tamanho_maximo: apiConfig.tamanho_maximo || ''
        });
      } else {
        // Se não há configurações, usar valores padrão
        setConfig({
          nome_site: 'HelpDesk',
          arquivos_permitidos: 'jpg, png, pdf, doc, docx, xls, xlsx',
          maximo_arquivos: '5',
          tamanho_maximo: '10'
        });
      }
      
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      toast.error('Erro ao carregar configurações');
      
      // Usar dados padrão como fallback
      setConfig({
        nome_site: 'HelpDesk',
        arquivos_permitidos: 'jpg, png, pdf, doc, docx, xls, xlsx',
        maximo_arquivos: '5',
        tamanho_maximo: '10'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    
    try {
      setIsSaving(true);
      
      // Salvar cada configuração individualmente
      const configData = [
        { nome: 'nome_site', valor: config.nome_site },
        { nome: 'arquivos_permitidos', valor: config.arquivos_permitidos },
        { nome: 'maximo_arquivos', valor: config.maximo_arquivos },
        { nome: 'tamanho_maximo', valor: config.tamanho_maximo }
      ];
      
      for (const item of configData) {
        try {
          await ConfiguracoesService.updateConfiguracaoPorNome(item.nome, { valor: item.valor });
        } catch (error) {
          console.error(`Erro ao atualizar ${item.nome}:`, error);
          // Tentar criar se não existir
          try {
            await ConfiguracoesService.createConfiguracao(item);
          } catch (createError) {
            console.error(`Erro ao criar ${item.nome}:`, createError);
          }
        }
      }
      
      toast.success('Configurações salvas com sucesso!');
      
      // Recarregar configurações para garantir sincronização
      await loadConfigurations();
      
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      toast.error('Erro ao salvar configurações');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Configurações
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Configure as opções gerais do sistema e PWA
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('general')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'general'
                  ? 'border-primary-500 dark:border-primary-400 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Settings className="h-5 w-5" />
                <span>Configurações Gerais</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('pwa')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'pwa'
                  ? 'border-primary-500 dark:border-primary-400 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Smartphone className="h-5 w-5" />
                <span>PWA</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('sistema')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'sistema'
                  ? 'border-primary-500 dark:border-primary-400 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Monitor className="h-5 w-5" />
                <span>Sistema</span>
              </div>
            </button>
          </nav>
        </div>

        <div className="p-6">
          {/* Tab Content - Configurações Gerais */}
          {activeTab === 'general' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6">
                Configurações Gerais
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="nome_site" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Nome do Site *
                  </label>
                  <div className="relative">
                    <Settings className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={20} />
                    <input
                      type="text"
                      id="nome_site"
                      value={config.nome_site}
                      onChange={(e) => setConfig(prev => ({ ...prev, nome_site: e.target.value }))}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                      placeholder="Digite o nome do site"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="arquivos_permitidos" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Tipos de Arquivo Permitidos *
                  </label>
                  <div className="relative">
                    <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={20} />
                    <input
                      type="text"
                      id="arquivos_permitidos"
                      value={config.arquivos_permitidos}
                      onChange={(e) => setConfig(prev => ({ ...prev, arquivos_permitidos: e.target.value }))}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                      placeholder="Ex: jpg, png, pdf, doc"
                      required
                    />
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Separe os tipos de arquivo por vírgula
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="maximo_arquivos" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Máximo de Arquivos *
                    </label>
                    <div className="relative">
                      <Upload className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={20} />
                      <input
                        type="number"
                        id="maximo_arquivos"
                        value={config.maximo_arquivos}
                        onChange={(e) => setConfig(prev => ({ ...prev, maximo_arquivos: e.target.value }))}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                        placeholder="Ex: 5"
                        min="1"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="tamanho_maximo" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Tamanho Máximo (MB) *
                    </label>
                    <div className="relative">
                      <Database className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={20} />
                      <input
                        type="number"
                        id="tamanho_maximo"
                        value={config.tamanho_maximo}
                        onChange={(e) => setConfig(prev => ({ ...prev, tamanho_maximo: e.target.value }))}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                        placeholder="Ex: 10"
                        min="1"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
                  <button 
                    type="submit" 
                    disabled={isSaving}
                    className="btn btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSaving ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Salvando...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Salvar Configurações
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Tab Content - Configurações PWA */}
          {activeTab === 'pwa' && (
            <PWASettings />
          )}

          {/* Tab Content - Sistema */}
          {activeTab === 'sistema' && (
            <SistemaStats />
          )}
        </div>
      </div>
    </div>
  );
};

export default Configurations;
